import {
  createTheme,
  darken,
  lighten,
  Theme,
  ThemeOptions,
} from '@mui/material';
import { deepmerge } from '@mui/utils';
import { atom, useAtomValue } from 'jotai';
import { useEffect, useMemo } from 'react';
import { authenticateMerchantConfigPlaceholder } from '../constants';
import { secureHttpClient } from '../restClient';
import { root } from '../Theme';
import { addToContext } from './contextManager';
import logger from './loggerManager';
import { environment } from '../environment';

export type SettleOptions = 'Automatic' | 'Manual' | 'AutomaticAndManual';
export type CartTypeOptions = 'Basic' | 'Advanced' | 'BasicAndAdvanced';
export interface AppOptions {
  citiExperience: boolean;
  flipScreen: boolean;
  merchantOrderIdRequired: boolean;
  consumersTab: boolean;
  pendingTab: boolean;
  transactionsTab: boolean;
  searchConsumersAcrossAllStores: boolean;
  showTransactionId: boolean;
  support: boolean;
  pin: boolean;
  description?: boolean;
  consumerSelfApply: boolean;
  displayMerchantLogo: boolean;
  interactionOptions: {
    apply: boolean;
    checkout: boolean;
    expressCheckout: boolean;
    applyLink: boolean;
    checkoutLink: boolean;
    expressCheckoutLink: boolean;
    applyQrCode: boolean;
    checkoutQrCode: boolean;
    expressCheckoutQrCode: boolean;
    applySms: boolean;
    checkoutSms: boolean;
    expressCheckoutSms: boolean;
  };
}

export interface ConfigManager {
  authOrganizationId: string;
  authClientId: string;
  peerId: string;
  peerName: string;
  peerPublicKey: string;
  currency: string;
  linkExpirationInSeconds?: {
    Url?: number;
    Email?: number;
  };
  configuration: {
    options: AppOptions;
    palette: {
      primary: string;
    };
    settings: {
      cartType: CartTypeOptions;
      selectedSettleOption?: SettleOptions;
      idleTimeInMinutes: number;
    };
  };
}

const HostName = atom<string>(window.location.origin.toLowerCase());
const urlAtom = atom(async () => {
  const { url } = await secureHttpClient.getOperate<{ url: string }>(
    {
      fileName: 'configManager.ts',
      fetchingFunction: 'urlAtom',
    },
    'api/peers/app-url/SalesApp',
  );
  return url;
});

export const appDataByMerchant = atom<Promise<ConfigManager>>(async () => {
  try {
    const hostname = window.location.hostname.split('.')[0]?.toLowerCase();
    if (hostname === 'authenticate') {
      return authenticateMerchantConfigPlaceholder;
    }
    const currentTime = Date.now();
    const response = await fetch(
      `${environment.backendBaseUrl}/v2/operate/api/peers-apps/configuration?app=salesapp&t=${currentTime}`,
    );
    if (!response.ok)
      throw new Error(`${response.status} ${await response.text()}`);
    const appData: ConfigManager = await response.json();
    addToContext('merchantId', appData.peerId);

    logger.log(`Loaded merchant configuration`, {
      filename: 'configManager.ts',
      function: 'ConfigByMerchant',
      appConfig: appData,
    });
    return appData;
  } catch (e) {
    logger.error(
      `Failed to import configuration file. url: ${window.location.href}. error: ${e}`,
      {
        filename: 'configManager',
        function: 'ConfigByMerchant',
      },
    );
    return authenticateMerchantConfigPlaceholder;
  }
});

const muiThemeByMerchant = atom(async (get) => {
  const merchantConfig = await get(appDataByMerchant);
  const merchantTheme: ThemeOptions = {
    typography: {
      main: {
        color: merchantConfig.configuration.palette.primary,
        textShadow: `8px 8px 0 ${lighten(
          merchantConfig.configuration.palette.primary,
          0.8,
        )}`,
      },
      warning: {
        color: merchantConfig.configuration.palette.primary,
      },
    },
    palette: {
      primary: {
        main: merchantConfig.configuration.palette.primary,
      },
      secondary: {
        main: lighten(merchantConfig.configuration.palette.primary, 0.4),
        light: lighten(merchantConfig.configuration.palette.primary, 0.8),
      },
    },
    components: {
      MuiTab: {
        styleOverrides: {
          root: {
            '&.Mui-selected': {
              color: merchantConfig.configuration.palette.primary,
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            backgroundColor: lighten(
              merchantConfig.configuration.palette.primary,
              0.8,
            ),
            color: merchantConfig.configuration.palette.primary,
          },
          contained: {
            backgroundColor: merchantConfig.configuration.palette.primary,
          },
          outlinedSecondary: {
            border: `2px solid ${merchantConfig.configuration.palette.primary}`,
          },
        },
      },
      MuiStepConnector: {
        styleOverrides: {
          lineHorizontal: {
            borderColor: lighten(
              merchantConfig.configuration.palette.primary,
              0.8,
            ),
          },
        },
      },
      MuiStepLabel: {
        styleOverrides: {
          label: {
            color: `${merchantConfig.configuration.palette.primary} !important`,
          },
        },
      },
      MuiStepIcon: {
        styleOverrides: {
          root: {
            border: `2px solid ${merchantConfig.configuration.palette.primary}`,
            boxShadow: `2px 2px 0px ${darken(
              merchantConfig.configuration.palette.primary,
              0.2,
            )}`,
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            '& .Mui-checked + .MuiSwitch-track': {
              opacity: `1 !important`,
              color: darken(merchantConfig.configuration.palette.primary, 0.2),
              backgroundColor: `${lighten(
                merchantConfig.configuration.palette.primary,
                0.8,
              )} !important`,
            },

            '& .Mui-checked > .MuiSwitch-thumb': {
              backgroundColor: merchantConfig.configuration.palette.primary,
            },
          },
          checked: {
            color: merchantConfig.configuration.palette.primary,
          },
          thumb: {
            backgroundColor: merchantConfig.configuration.palette.primary,
          },
        },
      },
    },
  };
  const merchantMuiTheme: Theme = createTheme(deepmerge(root, merchantTheme));
  return merchantMuiTheme;
});

export const useGetCurrentHostName = (): string => {
  return useAtomValue(HostName);
};

export const useGetUrl = () => useAtomValue(urlAtom);

const useGetAppData = () => useAtomValue(appDataByMerchant);
const useGetAppConfiguration = () => useGetAppData().configuration;

export const useGetMerchantFullName = (): string => useGetAppData().peerName;
export const useGetMerchantId = (): string => useGetAppData().peerId;

export const useGetAuth0Data = (): {
  clientId: string;
  organizationId: string;
} => {
  const appConfig = useGetAppData();
  return {
    organizationId: appConfig.authOrganizationId,
    clientId: appConfig.authClientId ?? environment.auth0ClientId,
  };
};

export const useGetMerchantTheme = (): Theme => {
  const theme = useAtomValue(muiThemeByMerchant);
  return useMemo(() => ({ ...theme }), [theme]);
};

export const useGetMerchantEnvPublicKey = (): string => {
  return useGetAppData().peerPublicKey;
};

export const useGetMerchantCurrency = (): string =>
  useGetAppData().currency ?? 'USD';

export const useGetMerchantLinkExpirations = () =>
  useGetAppData().linkExpirationInSeconds;

export const useGetMerchantConfigurationOptions = (): AppOptions =>
  useGetAppConfiguration().options;

export const useGetMerchantConfigurationSettings = () =>
  useGetAppConfiguration().settings;

export const useGetInteractionOptions = () =>
  useGetMerchantConfigurationOptions().interactionOptions;

export const useGetSearchConsumersAcrossAllStores = (): boolean =>
  useGetMerchantConfigurationOptions().searchConsumersAcrossAllStores ?? true;

export const useGetShowCartToggle = (): boolean =>
  useGetMerchantConfigurationSettings()?.cartType === 'BasicAndAdvanced';

export const useGetMerchantIdleTimeInMinutes = (): number =>
  useGetMerchantConfigurationSettings().idleTimeInMinutes;

export const useGetSelectedSettleOption = (): SettleOptions =>
  useGetMerchantConfigurationSettings().selectedSettleOption ?? 'Manual';

export const useSetupMerchantEnvironment = (): void => {
  const merchantFullName = useGetMerchantFullName();
  const { displayMerchantLogo, support } = useGetMerchantConfigurationOptions();

  const merchantId = useGetMerchantId();

  useEffect(() => {
    if (displayMerchantLogo) {
      const faviconUrl = getImageUrl('merchants', merchantId, 'favicon.png');
      document.title = merchantFullName;
      document.getElementById('favicon')?.setAttribute('href', faviconUrl);
      document.getElementById('appleIcon')?.setAttribute('href', faviconUrl);
    } else {
      document.title = 'SalesApp';
    }

    if (support) {
      const zendeskScript = document.createElement('script');
      zendeskScript.id = 'ze-snippet';
      zendeskScript.src =
        'https://static.zdassets.com/ekr/snippet.js?key=ce74fe34-d07e-4d2a-a652-3e41709222ba';
      zendeskScript.async = true;
      document.body.appendChild(zendeskScript);
    }
  }, [merchantFullName, merchantId, displayMerchantLogo, support]);
};

export const getImageUrl = (
  type: 'merchants' | 'lenders' = 'merchants',
  id: string,
  filename: 'salesAppLogo.png' | 'favicon.png' | 'logo_default.svg',
) => {
  return `${environment.storageUrl}/${type}/${id}/${filename}`;
};
